import React, { useState, useEffect } from 'react';
import { CreditCard, Loader2, Check, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok Pixel tracking helper
const trackTikTokEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track(eventName, eventData);
  }
};

// Google Analytics 4 tracking helper
const trackGA4Event = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventData);
  }
};

// Meta Pixel tracking helper
const trackMetaEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, eventData);
  }
};

const PaymentModal = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [config, setConfig] = useState({ amount: 12.69, currency: 'AUD' });

  // Fetch config from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stream/config`);
        if (response.ok) {
          const data = await response.json();
          setConfig({
            amount: data.amount,
            currency: data.currency.toUpperCase()
          });
        }
      } catch (error) {
        console.error('Failed to fetch config:', error);
      }
    };
    
    if (isOpen) {
      fetchConfig();
      // Track TikTok ViewContent Event when modal opens
      trackTikTokEvent('ViewContent', {
        content_type: 'product',
        content_id: 'washed-away-stream',
        content_name: 'Washed Away Documentary Stream'
      });
      // Track GA4 view_item Event when modal opens
      trackGA4Event('view_item', {
        currency: 'AUD',
        value: 12.69,
        items: [{
          item_id: 'washed-away-stream',
          item_name: 'Washed Away Documentary Stream',
          price: 12.69
        }]
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions to proceed');
      return;
    }

    setIsProcessing(true);

    try {
      // Create Stripe Checkout session
      const response = await fetch(`${API_URL}/api/payment/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          terms_accepted: termsAccepted,
          origin_url: window.location.origin,
          promo_code: promoCode || null
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Store email and session for later reference
      localStorage.setItem('pendingPaymentEmail', email);
      localStorage.setItem('pendingSessionId', data.session_id);
      localStorage.setItem('stripeCheckoutUrl', data.url);
      
      // Track TikTok InitiateCheckout Event
      trackTikTokEvent('InitiateCheckout', {
        content_type: 'product',
        content_id: 'washed-away-stream',
        content_name: 'Washed Away Documentary Stream',
        quantity: 1,
        price: config.amount,
        value: config.amount,
        currency: config.currency
      });
      
      // Track Meta Pixel InitiateCheckout Event
      trackMetaEvent('InitiateCheckout', {
        content_type: 'product',
        content_ids: ['washed-away-stream'],
        content_name: 'Washed Away Documentary Stream',
        num_items: 1,
        value: config.amount,
        currency: config.currency
      });
      
      // Track GA4 begin_checkout Event
      trackGA4Event('begin_checkout', {
        currency: config.currency,
        value: config.amount,
        items: [{
          item_id: 'washed-away-stream',
          item_name: 'Washed Away Documentary Stream',
          price: config.amount,
          quantity: 1
        }]
      });
      
      // Redirect directly to Stripe checkout - most reliable method
      toast.info('Redirecting to Stripe checkout...');
      window.location.href = data.url;

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Payment initialization failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPromoCode('');
    setTermsAccepted(false);
    setIsProcessing(false);
  };

  const handleClose = () => {
    if (!isProcessing) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="payment-modal" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="payment-modal-title">
            Watch Washed Away
          </DialogTitle>
          <DialogDescription className="payment-modal-description">
            One-time stream access — support independent documentary filmmaking
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="payment-form" data-testid="payment-form">
          {/* Price Display */}
          <div className="price-display">
            <span className="price-label">Stream Access</span>
            <span className="price-amount">
              ${config.amount.toFixed(2)} {config.currency}
            </span>
          </div>

          {/* Email Input */}
          <div className="form-group">
            <Label htmlFor="email" className="form-label">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isProcessing}
              className="form-input"
              data-testid="email-input"
            />
            <p className="form-help-text">
              Your magic link and stream ticket will be sent to this address
            </p>
          </div>

          {/* Promo Code Input */}
          <div className="form-group">
            <Label htmlFor="promo" className="form-label">
              Promo Code (optional)
            </Label>
            <Input
              id="promo"
              type="text"
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              disabled={isProcessing}
              className="form-input"
              data-testid="promo-input"
              style={{ textTransform: 'uppercase' }}
            />
            <p className="form-help-text">
              Have a discount code? Enter it here or on the Stripe checkout page
            </p>
          </div>

          {/* Payment Method Info */}
          <div className="form-group">
            <Label className="form-label">Payment Method</Label>
            <div className="payment-info-box">
              <CreditCard className="h-5 w-5 text-electric-blue" />
              <span>Secure checkout via Stripe</span>
            </div>
            <p className="form-help-text">
              You'll be redirected to Stripe's secure payment page. Apple Pay, Google Pay, and all major cards accepted.
            </p>
          </div>

          {/* Terms Checkbox */}
          <div className="terms-group">
            <div className="terms-checkbox">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                disabled={isProcessing}
                data-testid="terms-checkbox"
              />
              <Label htmlFor="terms" className="terms-label">
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" className="terms-link">
                  Terms & Conditions
                </a>
                . I understand this is a single-use stream ticket registered to my device and email. It is non-refundable and non-transferable.
              </Label>
            </div>
          </div>

          {/* Security Badge */}
          <div className="security-notice">
            <AlertCircle className="h-4 w-4" />
            <span>Secure payment processing. Your card details are never stored on our servers.</span>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="submit-button"
            disabled={isProcessing || !termsAccepted || !email}
            size="lg"
            data-testid="checkout-button"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Redirecting to checkout...
              </>
            ) : (
              <>
                <Check className="mr-2 h-5 w-5" />
                Continue to Payment — ${config.amount.toFixed(2)} {config.currency}
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
